import { confirmAsync } from '../../utils/confirm';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import DatePicker from '../../components/DatePicker';
import UsageInfo from '../../components/UsageInfo';
import {
  getHousekeepingOdalar, updateOdaDurum,
  getLaundryKayitlar, addLaundryKayit, updateLaundryKayit, deleteLaundryKayit,
  apiRequest,
} from '../../services/api';
import { useKasa } from '../../context/KasaContext';
import { downloadExcel, printTable } from '../../utils/exportUtils';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';

// ── Sabitler ──────────────────────────────────────────────────────────────
const DURUMLAR         = ['Hepsi', 'Temiz', 'Kirli', 'Temizleniyor', 'Kontrol Bekliyor', 'Arızalı', 'Arıza Giderildi'];
const LAUNDRY_DURUMLAR = ['Hepsi', 'Yıkamada', 'Hazır', 'Teslim Edildi'];

// ── Yardımcılar ───────────────────────────────────────────────────────────
function pad(n) { return String(n).padStart(2, '0'); }
function bugunStr() { var d = new Date(); return pad(d.getDate()) + '.' + pad(d.getMonth() + 1) + '.' + d.getFullYear(); }
function ddmmyyyyToIso(s) {
  if (!s) return '';
  var p = s.split('.');
  if (p.length < 3) return '';
  return p[2] + '-' + p[1] + '-' + p[0];
}

// ── Durum renkleri ────────────────────────────────────────────────────────
var DURUM_RENK = {
  'Temiz':             'bg-emerald-100/10 text-emerald-600 border-emerald-200/20',
  'Kirli':             'bg-red-100/10 text-red-600 border-red-200/20',
  'Temizleniyor':      'bg-amber-100/10 text-amber-600 border-amber-200/20',
  'Kontrol Bekliyor':  'bg-blue-100/10 text-blue-600 border-blue-200/20',
  'Arızalı':           'bg-gray-100/10 text-gray-500 border-gray-200/20',
};
var DURUM_DOT = {
  'Temiz':             'bg-emerald-500 shadow-sm shadow-emerald-100',
  'Kirli':             'bg-red-500 shadow-sm shadow-red-100',
  'Temizleniyor':      'bg-amber-500 shadow-sm shadow-amber-100',
  'Kontrol Bekliyor':  'bg-blue-500 shadow-sm shadow-blue-100',
  'Arızalı':           'bg-gray-400 shadow-sm shadow-gray-100',
};
var LAUNDRY_DURUM_RENK = {
  'Teslim Edildi': 'bg-purple-100/10 text-purple-600 border-purple-200/20',
  'Yıkamada':      'bg-orange-100/10 text-orange-600 border-orange-200/20',
  'Hazır':         'bg-emerald-100/10 text-emerald-600 border-emerald-200/20',
};

// ── Oda Durum Modal ───────────────────────────────────────────────────────
function OdaDurumModal({ oda, onKaydet, onKapat, saving }) {
  var [durum, setDurum] = useState(oda.durum || 'Temiz');
  var SEL = 'w-full bg-gray-50/50 border-2 border-transparent hover:border-blue-400 focus:bg-white focus:border-blue-500 rounded-2xl px-4 py-3 pr-10 text-sm font-black text-gray-700 appearance-none cursor-pointer transition-all outline-none';

  return createPortal(
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md z-[9999] flex items-center justify-center p-4 transform-gpu">
      <div className="absolute inset-0" onClick={onKapat} />
      <div className="bg-white/90 backdrop-blur-xl rounded-[2.5rem] shadow-2xl w-full max-w-md border border-white/40 overflow-hidden relative z-10 animate-modal-in transform-gpu">
        <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100/50 bg-gray-50/30">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center text-white text-2xl shadow-xl shadow-blue-100 transform-gpu hover:rotate-6 transition-transform">🛏️</div>
            <div>
              <h2 className="text-lg font-black text-gray-900 tracking-tighter">Oda {oda.odaNo}</h2>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{oda.odaTipiAd} • {oda.kat}. Kat</p>
            </div>
          </div>
          <button onClick={onKapat} className="w-10 h-10 flex items-center justify-center rounded-2xl bg-white/80 backdrop-blur-md text-gray-400 hover:bg-gray-900 hover:text-white transition-all text-xl font-bold shadow-sm border border-gray-100 transform-gpu active:scale-90">×</button>
        </div>
        <div className="px-8 py-8 space-y-6">
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase block mb-2 px-1 tracking-widest">Yeni Oda Durumu</label>
            <div className="relative group">
              <select value={durum} onChange={function(e) { setDurum(e.target.value); }} className={SEL}>
                {DURUMLAR.filter(function(d) { return d !== 'Hepsi'; }).map(function(d) {
                  return <option key={d}>{d}</option>;
                })}
              </select>
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs transition-colors group-hover:text-blue-500">▼</span>
            </div>
          </div>
        </div>
        <div className="px-8 pb-8 flex gap-3">
          <button onClick={function() { onKaydet(oda.odaId, durum); }} disabled={saving}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-xl shadow-blue-100 transition-all active:scale-95 transform-gpu">
            {saving ? '⏳...' : 'Değişikliği Uygula'}
          </button>
          <button onClick={onKapat}
            className="px-6 py-4 bg-white border-2 border-gray-100 rounded-2xl text-[11px] font-black uppercase tracking-widest text-gray-500 hover:bg-gray-900 hover:text-white hover:border-gray-900 transition-all shadow-sm transform-gpu">
            İptal
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Laundry Modal ─────────────────────────────────────────────────────────
function LaundryModal({ kayit, personelListe, laundryKalemleri, onKaydet, onSil, onKapat, saving }) {
  var duzenleme = !!kayit?.laundryKayitId;
  var [kalemiId, setKalemiId] = useState(kayit?.laundryKalemiId || (laundryKalemleri[0]?.laundryKalemiId || ''));
  var [adet,     setAdet]     = useState(kayit?.adet || 1);
  var [personelId, setPersonelId] = useState(kayit?.personelId || '');
  var [durum,    setDurum]    = useState(kayit?.durum || 'Yıkamada');
  var [tarih,    setTarih]    = useState(kayit?.tarih || bugunStr());

  var SEL = 'w-full bg-gray-50/50 border-2 border-transparent hover:border-purple-400 focus:bg-white focus:border-purple-500 rounded-2xl px-4 py-3 pr-10 text-sm font-black text-gray-700 appearance-none cursor-pointer transition-all outline-none';
  var INP = 'w-full bg-gray-50/50 border-2 border-transparent focus:bg-white focus:border-purple-500 rounded-2xl px-4 py-3 text-sm font-black text-gray-700 outline-none transition-all';

  function handleKaydet() {
    onKaydet({
      laundryKayitId: kayit?.laundryKayitId,
      laundryKalemiId: parseInt(kalemiId) || 1,
      adet: parseInt(adet) || 1,
      personelId: personelId ? parseInt(personelId) : null,
      durum,
      kayitTarihi: ddmmyyyyToIso(tarih) + 'T00:00:00',
    });
  }

  return createPortal(
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md z-[9999] flex items-center justify-center p-4 transform-gpu">
      <div className="absolute inset-0" onClick={onKapat} />
      <div className="bg-white/90 backdrop-blur-xl rounded-[2.5rem] shadow-2xl w-full max-w-md border border-white/40 overflow-hidden flex flex-col max-h-[90vh] relative z-10 animate-modal-in transform-gpu">
        <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100/50 bg-gray-50/30">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-fuchsia-600 rounded-2xl flex items-center justify-center text-white text-2xl shadow-xl shadow-purple-100 transform-gpu hover:rotate-6 transition-transform">👕</div>
            <h2 className="text-lg font-black text-gray-900 tracking-tighter">{duzenleme ? 'Laundry Düzenle' : 'Laundry Talebi'}</h2>
          </div>
          <button onClick={onKapat} className="w-10 h-10 flex items-center justify-center rounded-2xl bg-white/80 backdrop-blur-md text-gray-400 hover:bg-gray-900 hover:text-white transition-all text-xl font-bold shadow-sm border border-gray-100 transform-gpu active:scale-90">×</button>
        </div>
        <div className="px-8 py-8 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
          {/* Kategori */}
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase block mb-2 px-1 tracking-widest">Ürün Kategorisi</label>
            <div className="relative group">
              <select value={kalemiId} onChange={function(e) { setKalemiId(e.target.value); }} className={SEL}>
                {laundryKalemleri.map(function(k) {
                  return <option key={k.laundryKalemiId} value={k.laundryKalemiId}>{k.ad}</option>;
                })}
              </select>
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs transition-colors group-hover:text-purple-500">▼</span>
            </div>
          </div>
          {/* Adet */}
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase block mb-2 px-1 tracking-widest">Ürün Adedi</label>
            <div className="flex items-center gap-3">
              <button onClick={function() { setAdet(function(a) { return Math.max(1, a - 1); }); }}
                className="w-12 h-12 bg-gray-100 rounded-xl text-gray-600 hover:bg-gray-900 hover:text-white font-black text-xl flex items-center justify-center transition-all shadow-sm active:scale-90 transform-gpu">−</button>
              <input type="number" min="1" value={adet} onChange={function(e) { setAdet(parseInt(e.target.value) || 1); }}
                className="flex-1 bg-white border-2 border-gray-100 rounded-xl px-4 py-3 text-sm font-black text-center focus:border-purple-500 outline-none transition-all shadow-inner" />
              <button onClick={function() { setAdet(function(a) { return a + 1; }); }}
                className="w-12 h-12 bg-gray-100 rounded-xl text-gray-600 hover:bg-gray-900 hover:text-white font-black text-xl flex items-center justify-center transition-all shadow-sm active:scale-90 transform-gpu">+</button>
            </div>
          </div>
          {/* Personel */}
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase block mb-2 px-1 tracking-widest">Sorumlu Personel</label>
            <div className="relative group">
              <select value={personelId} onChange={function(e) { setPersonelId(e.target.value); }} className={SEL}>
                <option value="">— Personel Seçiniz —</option>
                {personelListe.map(function(p) {
                  return <option key={p.personelId} value={p.personelId}>{p.adSoyad}</option>;
                })}
              </select>
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs transition-colors group-hover:text-purple-500">▼</span>
            </div>
          </div>
          {/* Durum */}
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase block mb-2 px-1 tracking-widest">Güncel Durum</label>
            <div className="relative group">
              <select value={durum} onChange={function(e) { setDurum(e.target.value); }} className={SEL}>
                {['Yıkamada', 'Hazır', 'Teslim Edildi'].map(function(d) { return <option key={d}>{d}</option>; })}
              </select>
              <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs transition-colors group-hover:text-purple-500">▼</span>
            </div>
          </div>
          {/* Tarih */}
          <div>
            <label className="text-[10px] font-black text-gray-400 uppercase block mb-2 px-1 tracking-widest">Kayıt Tarihi</label>
            <DatePicker value={tarih} onChange={setTarih} />
          </div>
        </div>
        <div className="px-8 py-6 bg-gray-50/50 border-t border-gray-100/50 flex gap-3 transform-gpu">
          <button onClick={handleKaydet} disabled={saving}
            className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-xl shadow-purple-100 transition-all active:scale-95 transform-gpu">
            {saving ? '⏳...' : 'Kaydet'}
          </button>
          {duzenleme && (
            <button onClick={function() { onSil(kayit.laundryKayitId); }} disabled={saving}
              className="px-6 py-4 bg-red-50 text-red-600 border-2 border-red-100 hover:bg-red-600 hover:text-white hover:border-red-600 rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all transform-gpu">
              SİL
            </button>
          )}
          <button onClick={onKapat}
            className="px-6 py-4 bg-white border-2 border-gray-100 rounded-2xl text-[11px] font-black uppercase tracking-widest text-gray-500 hover:bg-gray-900 hover:text-white hover:border-gray-900 transition-all shadow-sm transform-gpu">
            İptal
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Özet Kart ─────────────────────────────────────────────────────────────
function OzetKart({ label, value, bgClass, textClass, icon, alt }) {
  return (
    <div className={`relative overflow-hidden rounded-[2.5rem] p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl group ${bgClass} border border-white/40 shadow-lg flex flex-col justify-between transform-gpu`}>
      <div className="absolute -right-4 -bottom-4 text-7xl opacity-5 group-hover:scale-125 group-hover:-rotate-12 transition-transform duration-500 will-change-transform">{icon}</div>
      <div className="flex items-start justify-between mb-2">
        <span className={`text-[10px] font-black uppercase tracking-[0.2em] ${textClass}`}>{label}</span>
        <span className="w-10 h-10 rounded-2xl flex items-center justify-center text-xl bg-white/80 backdrop-blur-md shadow-sm font-emoji flex-shrink-0">{icon}</span>
      </div>
      <div className="mt-2 text-2xl font-black text-gray-900 tracking-tighter leading-none">{value}</div>
      {alt && <p className={`text-[9px] font-black mt-3 tracking-widest uppercase opacity-80 ${textClass}`}>{alt}</p>}
    </div>
  );
}

// ── Ana Sayfa ─────────────────────────────────────────────────────────────
export default function HousekeepingKirli() {
  var { personeller } = useKasa();
  var [aktifTab, setAktifTab] = useState('odalar');

  // Oda state
  var [odalar,      setOdalar]      = useState([]);
  var [odaLoading,  setOdaLoading]  = useState(false);
  var [odaError,    setOdaError]    = useState(null);
  var [odaSaving,   setOdaSaving]   = useState(false);

  // Oda filtreleri
  var [filDurum,    setFilDurum]    = useState('Hepsi');
  var [filTip,      setFilTip]      = useState('Hepsi');
  var [filOdaNo,    setFilOdaNo]    = useState('');
  var [odaModal,    setOdaModal]    = useState(null);
  var [secili,      setSecili]      = useState({});

  // Laundry state
  var [laundry,         setLaundry]         = useState([]);
  var [laundryToplamKayit, setLaundryToplamKayit] = useState(0);
  var [laundrySayfa,    setLaundrySayfa]    = useState(1);
  var [laundryLoading,  setLaundryLoading]  = useState(false);
  var [laundrySaving,   setLaundrySaving]   = useState(false);
  var [laundryError,    setLaundryError]    = useState(null);

  // Laundry filtreleri
  var [filLTarih,    setFilLTarih]    = useState('');
  var [filLDurum,    setFilLDurum]    = useState('Hepsi');
  var [filLPersonelId, setFilLPersonelId] = useState('');
  var [laundryModal, setLaundryModal] = useState(null);

  // Yardımcı listeler
  var [laundryKalemleri, setLaundryKalemleri] = useState([]);

  var laundrySayfaBoyutu = 100;

  // Laundry kalemleri yükle
  useEffect(function() {
    apiRequest('LaundryKalemi').then(function(d) { setLaundryKalemleri(Array.isArray(d) ? d : []); }).catch(function() {});
  }, []);

  // Oda listesini yükle
  var fetchOdalar = useCallback(function() {
    setOdaLoading(true);
    setOdaError(null);
    getHousekeepingOdalar().then(function(data) {
      setOdalar((Array.isArray(data) ? data : []).sort((a, b) => String(a.odaNo || '').localeCompare(String(b.odaNo || ''), undefined, { numeric: true, sensitivity: 'base' })));
    }).catch(function() {
      setOdaError('Oda listesi alınamadı.');
    }).finally(function() {
      setOdaLoading(false);
    });
  }, []);

  useEffect(function() {
    fetchOdalar();
  }, [fetchOdalar]);

  // Laundry listesini yükle
  var fetchLaundry = useCallback(function(pg) {
    setLaundryLoading(true);
    setLaundryError(null);
    var params = { sayfa: pg, sayfaBoyutu: laundrySayfaBoyutu };
    if (filLTarih)       params.tarih      = ddmmyyyyToIso(filLTarih);
    if (filLDurum !== 'Hepsi') params.durum = filLDurum;
    if (filLPersonelId)  params.personelId = parseInt(filLPersonelId);
    getLaundryKayitlar(params).then(function(data) {
      setLaundry((data && data.satirlar) ? data.satirlar : []);
      setLaundryToplamKayit((data && data.toplamKayit) ? data.toplamKayit : 0);
    }).catch(function() {
      setLaundryError('Laundry listesi alınamadı.');
    }).finally(function() {
      setLaundryLoading(false);
    });
  }, [filLTarih, filLDurum, filLPersonelId]);

  useEffect(function() {
    if (aktifTab === 'laundry') fetchLaundry(laundrySayfa);
  }, [aktifTab, laundrySayfa, fetchLaundry]);

  // Filtrelenmiş odalar
  var filtreliOdalar = useMemo(function() {
    return odalar.filter(function(o) {
      if (filDurum !== 'Hepsi' && o.durum !== filDurum) return false;
      if (filTip   !== 'Hepsi' && o.odaTipiAd !== filTip) return false;
      if (filOdaNo && !o.odaNo.includes(filOdaNo)) return false;
      return true;
    });
  }, [odalar, filDurum, filTip, filOdaNo]);

  // Özet
  var ozet = useMemo(function() {
    var say = { Temiz: 0, Kirli: 0, Temizleniyor: 0, 'Kontrol Bekliyor': 0 };
    odalar.forEach(function(o) { if (say[o.durum] !== undefined) say[o.durum]++; });
    return say;
  }, [odalar]);

  // Benzersiz oda tipleri
  var odaTipleri = useMemo(function() {
    var tipler = ['Hepsi'];
    odalar.forEach(function(o) { if (o.odaTipiAd && !tipler.includes(o.odaTipiAd)) tipler.push(o.odaTipiAd); });
    return tipler;
  }, [odalar]);

  // Oda durum güncelle
  async function handleOdaKaydet(odaId, durum) {
    setOdaSaving(true);
    try {
      await updateOdaDurum(odaId, durum);
      setOdaModal(null);
      setSecili({});
      fetchOdalar();
    } catch {
      setOdaError('Oda durumu güncellenemedi.');
    } finally {
      setOdaSaving(false);
    }
  }

  // Toplu durum güncelle
  async function topluGuncelle(yeniDurum) {
    var seciliIds = Object.entries(secili).filter(function(e) { return e[1]; }).map(function(e) { return parseInt(e[0]); });
    if (seciliIds.length === 0) return;
    setOdaSaving(true);
    try {
      await Promise.all(seciliIds.map(function(id) { return updateOdaDurum(id, yeniDurum); }));
      setSecili({});
      fetchOdalar();
    } catch {
      setOdaError('Toplu güncelleme başarısız.');
    } finally {
      setOdaSaving(false);
    }
  }

  // Laundry kaydet
  async function handleLaundryKaydet(data) {
    setLaundrySaving(true);
    try {
      if (data.laundryKayitId) {
        await updateLaundryKayit(data.laundryKayitId, data);
      } else {
        await addLaundryKayit(data);
      }
      setLaundryModal(null);
      fetchLaundry(laundrySayfa);
    } catch {
      setLaundryError('Laundry kaydı işlemi başarısız.');
    } finally {
      setLaundrySaving(false);
    }
  }

  async function handleLaundrySil(id) {
    if (!(await confirmAsync())) return;
    setLaundrySaving(true);
    try {
      await deleteLaundryKayit(id);
      setLaundryModal(null);
      fetchLaundry(laundrySayfa);
    } catch {
      setLaundryError('Silme işlemi başarısız.');
    } finally {
      setLaundrySaving(false);
    }
  }

  function handleExcelOdalar() {
    var baslik = ['Oda No', 'Oda Tipi', 'Kat', 'Durum', 'Açıklama'];
    var satirlar = filtreliOdalar.map(function(o) {
      return [o.odaNo, o.odaTipiAd, o.kat + '. Kat', o.durum, o.aciklama || ''];
    });
    downloadExcel('housekeeping-odalar', 'Oda Listesi', baslik, satirlar);
  }

  function handleExcelLaundry() {
    var baslik = ['Kategori', 'Adet', 'Personel', 'Durum', 'Tarih'];
    var satirlar = laundry.map(function(l) {
      return [l.kategoriAd, l.adet, l.personelAdSoyad || '—', l.durum, l.tarih];
    });
    downloadExcel('laundry-kayitlar', 'Laundry Kayıtları', baslik, satirlar);
  }

  function handleYazdir() {
    var baslik = ['Oda No', 'Oda Tipi', 'Kat', 'Durum', 'Açıklama'];
    var satirlar = filtreliOdalar.map(function(o) {
      return [o.odaNo, o.odaTipiAd, o.kat + '. Kat', o.durum, o.aciklama || ''];
    });
    printTable('Housekeeping Oda Listesi', baslik, satirlar, filtreliOdalar.length + ' oda');
  }

  var seciliSayi = Object.values(secili).filter(Boolean).length;
  var tumunuSec  = filtreliOdalar.length > 0 && filtreliOdalar.every(function(o) { return secili[o.odaId]; });
  var laundryToplamSayfa = Math.ceil(laundryToplamKayit / laundrySayfaBoyutu);

  var SEL = 'border border-gray-300 rounded-xl px-3 py-2 pr-8 text-sm bg-white focus:outline-none appearance-none cursor-pointer font-medium';

  return (
    <div className="p-3 md:p-6 space-y-8 min-h-screen bg-gray-50/50 transform-gpu animate-fade-in">

      {/* Premium Header */}
      <div className="relative overflow-hidden bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-xl p-4 md:p-8 transform-gpu">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-teal-100 rounded-full blur-[80px] opacity-50"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-cyan-100 rounded-full blur-[80px] opacity-40"></div>
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="flex items-center gap-6 min-w-0">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-700 rounded-2xl flex-shrink-0 flex items-center justify-center text-white text-3xl shadow-2xl shadow-teal-200 transform-gpu hover:rotate-6 transition-transform">🧹</div>
            <div className="min-w-0">
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tighter truncate">Housekeeping Operasyonları</h1>
              <p className="text-sm font-bold text-gray-400 mt-1 uppercase tracking-widest truncate">Oda Temizlik & Çamaşır Takip İşlemleri</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 items-center">
            {aktifTab === 'odalar' ? (
              <button onClick={handleExcelOdalar} className="bg-white/80 backdrop-blur-md border-2 border-emerald-100/50 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700 font-black px-6 py-4 rounded-2xl shadow-lg shadow-emerald-50 transition-all text-[10px] uppercase tracking-widest hover:-translate-y-1 active:scale-95 whitespace-nowrap transform-gpu">
                <span>📊</span> ODA LİSTESİ EXCEL
              </button>
            ) : (
              <button onClick={handleExcelLaundry} className="bg-white/80 backdrop-blur-md border-2 border-emerald-100/50 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700 font-black px-6 py-4 rounded-2xl shadow-lg shadow-emerald-50 transition-all text-[10px] uppercase tracking-widest hover:-translate-y-1 active:scale-95 whitespace-nowrap transform-gpu">
                <span>📊</span> LAUNDRY EXCEL
              </button>
            )}
            <button onClick={handleYazdir} className="bg-gray-800 hover:bg-black text-white rounded-2xl px-6 py-4 text-[10px] font-black transition-all uppercase tracking-widest shadow-xl shadow-gray-200 hover:-translate-y-1 active:scale-95 transform-gpu">
              <span>🖨️</span> YAZDIR
            </button>
          </div>
        </div>
      </div>

      {/* Özet Kartlar */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 xl:gap-5 relative z-10 transform-gpu">
        <OzetKart label="Temiz" value={ozet['Temiz'] + ' oda'} bgClass="bg-gradient-to-br from-emerald-50/50 to-green-50/50" textClass="text-emerald-600" icon="✅" alt="Müsait / Hazır" />
        <OzetKart label="Kirli" value={ozet['Kirli'] + ' oda'} bgClass="bg-gradient-to-br from-red-50/50 to-rose-50/50" textClass="text-red-500" icon="🔴" alt="Temizlik Bekliyor" />
        <OzetKart label="Temizleniyor" value={ozet['Temizleniyor'] + ' oda'} bgClass="bg-gradient-to-br from-amber-50/50 to-orange-50/50" textClass="text-amber-600" icon="🧹" alt="Personel Aktif İşlemde" />
        <OzetKart label="Kontrol Bekliyor" value={ozet['Kontrol Bekliyor'] + ' oda'} bgClass="bg-gradient-to-br from-blue-50/50 to-indigo-50/50" textClass="text-blue-600" icon="🔍" alt="Süpervizör Onayı Bekliyor" />
      </div>

      {/* Modern Premium Sekmeler */}
      <div className="flex gap-2 bg-gray-100/30 backdrop-blur-md rounded-[2.5rem] p-2 w-fit border border-gray-200/50 transform-gpu">
        {[
          ['odalar', '🛏️ ODA LİSTESİ'], 
          ['laundry', '👕 LAUNDRY UYGULAMASI']
        ].map(tab => (
          <button key={tab[0]} onClick={() => setAktifTab(tab[0])}
            className={`px-8 py-4 rounded-[2rem] text-[10px] font-black tracking-widest uppercase transition-all duration-500 transform-gpu ${aktifTab === tab[0] ? 'bg-white text-teal-700 shadow-xl shadow-teal-100/20 scale-[1.02] border border-teal-50/50' : 'text-gray-500 hover:text-gray-900 hover:bg-white/50'}`}>
            {tab[1]}
          </button>
        ))}
      </div>

      {/* ══════════════ ODA LİSTESİ ══════════════ */}
      {aktifTab === 'odalar' && (
        <div className="space-y-6 transform-gpu">
          {odaError && <div className="bg-red-50/80 backdrop-blur-md border border-red-200/50 text-red-700 rounded-2xl px-5 py-4 text-sm font-bold shadow-lg shadow-red-50 animate-shake transform-gpu">⚠️ {odaError}</div>}

          {/* Akıllı Filtre */}
          <div className="bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-2xl p-6 md:p-8 z-20 relative transform-gpu">
            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
               <span className="w-2 h-2 bg-teal-500 rounded-full animate-pulse shadow-sm" /> Oda Filtreleme Aracı
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 items-end">
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase block mb-2.5 tracking-widest ml-1">Oda No / Ara</label>
                <input value={filOdaNo} onChange={e => setFilOdaNo(e.target.value)}
                  placeholder="101..." className="w-full bg-gray-50/50 border-2 border-transparent focus:bg-white focus:border-teal-400 rounded-2xl px-4 py-3 text-sm font-black text-gray-700 outline-none transition-all placeholder-gray-300 shadow-inner" />
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase block mb-2.5 tracking-widest ml-1">Temizlik Durumu</label>
                <div className="relative group">
                  <select value={filDurum} onChange={e => setFilDurum(e.target.value)} className="w-full bg-gray-50/50 border-2 border-transparent group-hover:border-teal-400 focus:bg-white focus:border-teal-500 rounded-2xl px-4 py-3 pr-10 font-black text-sm text-gray-700 appearance-none cursor-pointer transition-all outline-none">
                    {DURUMLAR.map(d => <option key={d}>{d}</option>)}
                  </select>
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs transition-colors group-hover:text-teal-500">▼</span>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black text-gray-400 uppercase block mb-2.5 tracking-widest ml-1">Oda Tipi</label>
                <div className="relative group">
                  <select value={filTip} onChange={e => setFilTip(e.target.value)} className="w-full bg-gray-50/50 border-2 border-transparent group-hover:border-teal-400 focus:bg-white focus:border-teal-500 rounded-2xl px-4 py-3 pr-10 font-black text-sm text-gray-700 appearance-none cursor-pointer transition-all outline-none">
                    {odaTipleri.map(t => <option key={t}>{t}</option>)}
                  </select>
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs transition-colors group-hover:text-teal-500">▼</span>
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={() => { setFilDurum('Hepsi'); setFilTip('Hepsi'); setFilOdaNo(''); }}
                className="w-full sm:w-auto bg-gray-100/50 hover:bg-gray-900 hover:text-white rounded-2xl px-8 py-3.5 font-black text-[10px] uppercase tracking-widest transition-all active:scale-95 transform-gpu">
                Sıfırla
              </button>
            </div>
          </div>

          {/* Toplu işlem */}
          {seciliSayi > 0 && (
            <div className="bg-indigo-600/90 backdrop-blur-md text-white rounded-[2rem] px-8 py-5 flex items-center gap-6 flex-wrap shadow-2xl shadow-indigo-200 border border-indigo-400 animate-slide-up transform-gpu">
              <div className="flex items-center gap-3">
                 <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center font-black text-sm">{seciliSayi}</div>
                 <span className="text-[10px] font-black uppercase tracking-widest">Oda Seçildi</span>
              </div>
              <div className="h-8 w-px bg-white/20 hidden sm:block" />
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-black uppercase tracking-widest text-indigo-100">Toplu Güncelle:</span>
                {['Temiz', 'Kirli', 'Temizleniyor', 'Kontrol Bekliyor'].map(function(d) {
                  return (
                    <button key={d} onClick={function() { topluGuncelle(d); }} disabled={odaSaving}
                      className="text-[10px] font-black uppercase tracking-widest px-4 py-2.5 bg-white/10 hover:bg-white text-white hover:text-indigo-600 rounded-xl transition-all shadow-sm active:scale-90 transform-gpu">
                      {d}
                    </button>
                  );
                })}
              </div>
              <button onClick={function() { setSecili({}); }} className="ml-auto text-[10px] font-black uppercase tracking-widest text-indigo-200 hover:text-white transition-colors">Vazgeç</button>
            </div>
          )}

          {/* Tablo – Mobil Kartlar */}
          <div className="md:hidden space-y-4">
            {odaLoading ? (
              <LoadingSpinner fullPage text="Envanter Yükleniyor" color="cyan" />
            ) : filtreliOdalar.length === 0 ? (
              <EmptyState icon="📭" text="Kayıt bulunamadı" />
            ) : filtreliOdalar.map(function(oda) {
              var isSelected = !!secili[oda.odaId];
              return (
                <div key={oda.odaId} className={`bg-white/80 backdrop-blur-xl rounded-[2.5rem] border-2 shadow-xl p-5 transition-all transform-gpu animate-fade-in ${isSelected ? 'border-teal-400 ring-4 ring-teal-50' : 'border-white/40'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <input type="checkbox" checked={isSelected}
                          onChange={function(e) {
                            setSecili(function(prev) {
                              var next = Object.assign({}, prev);
                              if (e.target.checked) next[oda.odaId] = true;
                              else delete next[oda.odaId];
                              return next;
                            });
                          }}
                          className="w-6 h-6 rounded-lg border-2 border-gray-200 checked:bg-teal-500 checked:border-teal-500 transition-all cursor-pointer appearance-none" />
                        {isSelected && <span className="absolute inset-0 flex items-center justify-center text-white pointer-events-none text-xs font-black">✓</span>}
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">ODA NOSU</p>
                        <p className="font-black text-gray-900 text-lg leading-none">{oda.odaNo}</p>
                      </div>
                    </div>
                    <div className="text-right flex flex-col gap-1.5 items-end">
                       <span className={'text-[9px] font-black px-3 py-1.5 rounded-full flex items-center gap-1.5 uppercase tracking-widest border transition-all ' + (DURUM_RENK[oda.durum] || 'bg-gray-100/10 text-gray-500 border-gray-200/20')}>
                         <span className={'w-1.5 h-1.5 rounded-full ' + (DURUM_DOT[oda.durum] || 'bg-gray-400')}></span>
                         {oda.durum}
                       </span>
                       {oda.arizaliMi && (
                          <span className="text-[9px] font-black px-3 py-1.5 rounded-full flex items-center gap-1.5 uppercase tracking-widest border transition-all bg-amber-50 text-amber-600 border-amber-200/50 justify-end cursor-help" title={oda.arizaNedeni}>
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                            Arızalı {oda.arizaNedeni && `— ${oda.arizaNedeni}`}
                          </span>
                       )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 bg-gray-50/50 p-4 rounded-2xl mb-4 border border-white/50 shadow-inner">
                     <div>
                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1 font-mono">TİP / KONUM</p>
                        <p className="text-[10px] font-black text-gray-700 truncate">{oda.odaTipiAd} <span className="text-gray-300 mx-1">•</span> {oda.kat}. Kat</p>
                     </div>
                     <div>
                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1 font-mono">PERSONEL NOTU</p>
                        <p className="text-[10px] font-black text-gray-500 truncate italic">{oda.aciklama || 'Not yok'}</p>
                     </div>
                  </div>
                  <button onClick={function() { setOdaModal(oda); }}
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white text-[10px] font-black uppercase tracking-widest py-3 rounded-xl transition-all shadow-lg shadow-teal-100 transform-gpu active:scale-95">
                    DURUMU GÜNCELLE
                  </button>
                </div>
              );
            })}
          </div>

          {/* Tablo – Masaüstü */}
          <div className="hidden md:block bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-2xl overflow-hidden relative z-10 transform-gpu">
            <div className="flex items-center justify-between px-10 py-6 border-b border-gray-100/50 bg-gray-50/30">
              <div>
                <p className="text-sm font-black text-gray-900 uppercase tracking-tighter">Oda Envanter & Durum Grid'i</p>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                  Toplam <span className="text-teal-600 font-black">{filtreliOdalar.length}</span> ünite listeleniyor.
                </p>
              </div>
              <div className="flex items-center gap-6">
                 <div className="flex items-center gap-1.5 text-[9px] font-black text-gray-400 uppercase tracking-widest">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-sm" /> Temiz
                 </div>
                 <div className="flex items-center gap-1.5 text-[9px] font-black text-gray-400 uppercase tracking-widest">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-400 shadow-sm shadow-red-100" /> Kirli
                 </div>
              </div>
            </div>
            <div className="overflow-x-auto min-h-[400px]">
              <table className="w-full text-sm border-separate border-spacing-0">
                <thead>
                  <tr className="bg-gray-100/50">
                    <th className="py-6 px-6 w-14 border-b border-gray-100/50">
                      <div className="relative cursor-pointer group">
                        <input type="checkbox" checked={tumunuSec}
                          onChange={function(e) {
                            var yeni = {};
                            if (e.target.checked) filtreliOdalar.forEach(function(o) { yeni[o.odaId] = true; });
                            setSecili(yeni);
                          }}
                          className="w-6 h-6 rounded-lg border-2 border-gray-200 checked:bg-teal-500 checked:border-teal-500 transition-all cursor-pointer appearance-none shadow-sm" />
                        {tumunuSec && <span className="absolute inset-0 flex items-center justify-center text-white pointer-events-none text-xs font-black">✓</span>}
                      </div>
                    </th>
                    <th className="py-6 px-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100/50">Oda No</th>
                    <th className="py-6 px-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100/50">Oda Tipi</th>
                    <th className="py-6 px-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100/50">Kat</th>
                    <th className="py-6 px-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100/50">Durum</th>
                    <th className="py-6 px-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100/50">Yönetimden Not</th>
                    <th className="py-6 px-4 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100/50 w-24">Aksiyon</th>
                  </tr>
                </thead>
                <tbody>
                  {odaLoading ? (
                    <EmptyState as="td" colSpan={7} icon="🔄" text="Sistem Sorgulanıyor" />
                  ) : filtreliOdalar.length === 0 ? (
                    <EmptyState as="td" colSpan={7} icon="📁" text="Eşleşen ünite bulunamadı" />
                  ) : filtreliOdalar.map(function(oda, idx) {
                    var isSelected = !!secili[oda.odaId];
                    return (
                      <tr key={oda.odaId}
                        className={'group transition-all hover:bg-gray-50 transform-gpu ' +
                          (isSelected ? 'bg-teal-50/50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/20')}>
                        <td className="py-5 px-6 border-b border-gray-50/50">
                          <div className="relative group/check">
                            <input type="checkbox" checked={isSelected}
                              onChange={function(e) {
                                setSecili(function(prev) {
                                  var next = Object.assign({}, prev);
                                  if (e.target.checked) next[oda.odaId] = true;
                                  else delete next[oda.odaId];
                                  return next;
                                });
                              }}
                              className="w-6 h-6 rounded-lg border-2 border-gray-200 checked:bg-teal-500 checked:border-teal-500 transition-all cursor-pointer appearance-none shadow-sm" />
                            {isSelected && <span className="absolute inset-0 flex items-center justify-center text-white pointer-events-none text-xs font-black">✓</span>}
                          </div>
                        </td>
                        <td className="py-5 px-4 border-b border-gray-50/50">
                           <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-xl flex items-center justify-center font-black text-[11px] shadow-sm transform-gpu group-hover:rotate-6 transition-transform ${isSelected ? 'bg-teal-600 text-white' : 'bg-white border border-gray-100 text-gray-800'}`}>{oda.odaNo}</div>
                              <span className={`font-black text-[11px] ${isSelected ? 'text-teal-700' : 'text-gray-900'}`}>Oda {oda.odaNo}</span>
                           </div>
                        </td>
                        <td className="py-5 px-4 border-b border-gray-50/50">
                           <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{oda.odaTipiAd}</span>
                        </td>
                        <td className="py-5 px-4 border-b border-gray-50/50">
                           <span className="text-[10px] font-black text-gray-700 uppercase tracking-tighter group-hover:scale-110 transition-transform block w-fit">{oda.kat}. Kat</span>
                        </td>
                        <td className="py-5 px-4 border-b border-gray-50/50">
                          <div className="flex flex-col gap-1.5 w-fit">
                            <span className={'text-[9px] font-black px-3 py-1.5 rounded-full flex items-center gap-1.5 w-fit uppercase tracking-widest border transition-all ' + (DURUM_RENK[oda.durum] || 'bg-gray-100/10 text-gray-500 border-gray-200/20')}>
                              <span className={'w-1.5 h-1.5 rounded-full ' + (DURUM_DOT[oda.durum] || 'bg-gray-400')}></span>
                              {oda.durum}
                            </span>
                            {oda.arizaliMi && (
                              <span className="text-[9px] font-black px-3 py-1.5 rounded-full flex items-center gap-1.5 w-fit uppercase tracking-widest border transition-all bg-amber-50 text-amber-600 border-amber-200/50 max-w-[200px] cursor-help" title={oda.arizaNedeni}>
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse flex-shrink-0"></span>
                                <span className="truncate">Arızalı {oda.arizaNedeni && `— ${oda.arizaNedeni}`}</span>
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-5 px-4 border-b border-gray-50/50">
                           <p className="text-[10px] font-bold text-gray-400 italic max-w-[160px] truncate group-hover:text-gray-600 transition-colors">{oda.aciklama || <span className="opacity-20">—</span>}</p>
                        </td>
                        <td className="py-5 px-4 border-b border-gray-50/50 text-center">
                          <button onClick={function() { setOdaModal(oda); }}
                            className="text-[10px] font-black px-4 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-gray-900 hover:text-white rounded-xl transition-all shadow-sm active:scale-90 transform-gpu uppercase tracking-widest">
                            YÖNET
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════ LAUNDRY ══════════════ */}
      {aktifTab === 'laundry' && (
        <div className="space-y-6 transform-gpu animate-fade-in">
          {laundryError && <div className="bg-red-50/80 backdrop-blur-md border border-red-200/50 text-red-700 rounded-2xl px-5 py-4 text-sm font-bold shadow-lg shadow-red-50 animate-shake transform-gpu">⚠️ {laundryError}</div>}

          {/* Laundry Filtreleri */}
          <div className="bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-2xl p-6 md:p-8 z-20 relative transform-gpu">
            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-6 flex items-center gap-2">
               <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse shadow-sm" /> Laundry Operasyon Filtreleri
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-6 items-end">
              <div className="md:col-span-3">
                <label className="text-[10px] font-black text-gray-400 uppercase block mb-2.5 tracking-widest ml-1">Kayıt Tarihi</label>
                <DatePicker value={filLTarih} onChange={setFilLTarih} />
              </div>
              <div className="md:col-span-3">
                <label className="text-[10px] font-black text-gray-400 uppercase block mb-2.5 tracking-widest ml-1">Yıkama Durumu</label>
                <div className="relative group">
                  <select value={filLDurum} onChange={e => setFilLDurum(e.target.value)} className="w-full bg-gray-50/50 border-2 border-transparent group-hover:border-purple-400 focus:bg-white focus:border-purple-500 rounded-2xl px-4 py-3 font-black text-sm text-gray-700 appearance-none cursor-pointer transition-all outline-none">
                    {LAUNDRY_DURUMLAR.map(d => <option key={d}>{d}</option>)}
                  </select>
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs transition-colors group-hover:text-purple-500">▼</span>
                </div>
              </div>
              <div className="md:col-span-3">
                <label className="text-[10px] font-black text-gray-400 uppercase block mb-2.5 tracking-widest ml-1">Sorumlu Personel</label>
                <div className="relative group">
                  <select value={filLPersonelId} onChange={e => setFilLPersonelId(e.target.value)} className="w-full bg-gray-50/50 border-2 border-transparent group-hover:border-purple-400 focus:bg-white focus:border-purple-500 rounded-2xl px-4 py-3 font-black text-sm text-gray-700 appearance-none cursor-pointer transition-all outline-none">
                    <option value="">— Hepsi —</option>
                    {personeller.map(p => <option key={p.personelId} value={p.personelId}>{p.adSoyad}</option>)}
                  </select>
                  <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs transition-colors group-hover:text-purple-500">▼</span>
                </div>
              </div>
              <div className="md:col-span-3 flex flex-wrap gap-2">
                <button onClick={() => { setLaundrySayfa(1); fetchLaundry(1); }} className="flex-[2] bg-purple-600 hover:bg-purple-700 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl shadow-purple-100 transition-all py-3.5 active:scale-95 transform-gpu">
                  SORGULA
                </button>
                <button onClick={() => { setFilLTarih(''); setFilLDurum('Hepsi'); setFilLPersonelId(''); setLaundrySayfa(1); }}
                  className="flex-[1] bg-gray-100/50 hover:bg-gray-900 hover:text-white rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all py-3.5 active:scale-95 transform-gpu">
                  R
                </button>
              </div>
            </div>
          </div>

          <div className="flex justify-end pr-4 md:-mt-4 relative z-30 mb-4">
            <button onClick={() => setLaundryModal('ekle')}
              className="bg-gradient-to-br from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800 text-white px-8 py-4 rounded-[2rem] shadow-2xl shadow-purple-200 font-black text-[11px] uppercase tracking-widest transition-all hover:scale-105 active:scale-95 flex items-center gap-3 transform-gpu">
              <span className="text-2xl leading-none font-emoji">👕</span> YENİ LAUNDRY TALEBİ / GÖNDERİM
            </button>
          </div>

          {/* Laundry Özet */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 relative z-10 transform-gpu">
            <OzetKart label="Yıkamada" value={laundry.filter(l => l.durum === 'Yıkamada').reduce((s, l) => s + l.adet, 0) + ' adet'} bgClass="bg-gradient-to-br from-orange-50/50 to-amber-50/50" textClass="text-orange-500" icon="🔄" alt="Bekleyen Ürünler" />
            <OzetKart label="Hazır" value={laundry.filter(l => l.durum === 'Hazır').reduce((s, l) => s + l.adet, 0) + ' adet'} bgClass="bg-gradient-to-br from-emerald-50/50 to-green-50/50" textClass="text-emerald-500" icon="✅" alt="Teslime Hazır" />
            <OzetKart label="Teslim Edildi" value={laundry.filter(l => l.durum === 'Teslim Edildi').reduce((s, l) => s + l.adet, 0) + ' adet'} bgClass="bg-gradient-to-br from-purple-50/50 to-fuchsia-50/50" textClass="text-purple-600" icon="📦" alt="Hizmete Konanlar" />
          </div>

          {/* Laundry Tablo – Mobil Kartlar */}
          <div className="md:hidden space-y-4">
            {laundryLoading ? (
              <LoadingSpinner fullPage text="Kayıtlar Sorgulanıyor" color="violet" />
            ) : laundry.length === 0 ? (
              <EmptyState icon="🧺" text="Kayıt bulunamadı" />
            ) : laundry.map(function(l) {
              return (
                <div key={l.laundryKayitId} className="bg-white/80 backdrop-blur-xl rounded-[2.5rem] border-2 border-white/40 shadow-xl p-5 transition-all transform-gpu animate-fade-in hover:border-purple-200">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5 font-mono">#{l.laundryKayitId}</p>
                      <p className="font-black text-gray-900 text-lg leading-none">{l.kategoriAd}</p>
                    </div>
                    <span className={'text-[9px] font-black px-3 py-1.5 rounded-full flex items-center gap-1.5 uppercase tracking-widest border transition-all ' + (LAUNDRY_DURUM_RENK[l.durum] || 'bg-gray-100/10 text-gray-500')}>
                      {l.durum}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 bg-gray-50/50 p-4 rounded-2xl mb-4 border border-white/50 shadow-inner">
                     <div>
                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1 font-mono">MİKTAR</p>
                        <p className="text-[10px] font-black text-gray-700">{l.adet} Parça</p>
                     </div>
                     <div>
                        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1 font-mono">SORUMLU</p>
                        <p className="text-[10px] font-black text-gray-700 truncate">{l.personelAdSoyad || '—'}</p>
                     </div>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-[10px] font-black text-gray-400 tracking-widest uppercase">{l.tarih}</span>
                    <button onClick={function() { setLaundryModal(l); }}
                      className="bg-purple-600/10 text-purple-700 hover:bg-purple-600 hover:text-white px-6 py-2.5 rounded-xl font-black text-[10px] transition-all transform-gpu active:scale-95 uppercase tracking-widest">
                      Detay / Düzenle
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Laundry Tablo – Masaüstü */}
          <div className="hidden md:block bg-white/80 backdrop-blur-xl rounded-[2.5rem] border border-white/40 shadow-2xl overflow-hidden relative z-10 mt-8 transform-gpu">
            <div className="flex items-center justify-between px-10 py-6 border-b border-gray-100/50 bg-gray-50/30">
              <div>
                <p className="text-sm font-black text-gray-900 uppercase tracking-tighter">Laundry Operasyon Grid'i</p>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                  Toplam <span className="text-purple-600 font-black">{laundryToplamKayit}</span> kayıt listeleniyor.
                </p>
              </div>
              <div className="flex gap-2">
                <button onClick={function() { setLaundrySayfa(function(p) { return Math.max(1, p - 1); }); }}
                  disabled={laundrySayfa <= 1 || laundryLoading}
                  className="w-10 h-10 border-2 border-gray-100 rounded-xl flex items-center justify-center hover:bg-gray-900 hover:text-white transition-all disabled:opacity-30 transform-gpu active:scale-90 font-black">‹</button>
                <div className="px-4 flex items-center justify-center bg-gray-50 rounded-xl border border-gray-100 font-black text-[10px]">{laundrySayfa} / {laundryToplamSayfa || 1}</div>
                <button onClick={function() { setLaundrySayfa(function(p) { return Math.min(laundryToplamSayfa, p + 1); }); }}
                  disabled={laundrySayfa >= laundryToplamSayfa || laundryLoading}
                  className="w-10 h-10 border-2 border-gray-100 rounded-xl flex items-center justify-center hover:bg-gray-900 hover:text-white transition-all disabled:opacity-30 transform-gpu active:scale-90 font-black">›</button>
              </div>
            </div>
            <div className="overflow-x-auto min-h-[400px]">
              <table className="w-full text-sm border-separate border-spacing-0">
                <thead>
                  <tr className="bg-gray-100/50">
                    <th className="py-6 px-6 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100/50">Kategori</th>
                    <th className="py-6 px-4 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100/50 w-24">Adet</th>
                    <th className="py-6 px-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100/50">Sorumlu Personel</th>
                    <th className="py-6 px-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100/50">Durum</th>
                    <th className="py-6 px-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100/50">Kayıt Tarihi</th>
                    <th className="py-6 px-4 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100/50 w-24">Aksiyon</th>
                  </tr>
                </thead>
                <tbody>
                  {laundryLoading ? (
                    <EmptyState as="td" colSpan={6} icon="🔄" text="Arşiv Taranıyor" />
                  ) : laundry.length === 0 ? (
                    <EmptyState as="td" colSpan={6} icon="🧺" text="Laundry kaydı bulunamadı" />
                  ) : laundry.map(function(l, idx) {
                    return (
                      <tr key={l.laundryKayitId}
                        className={'group transition-all hover:bg-gray-50 transform-gpu ' + (idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/20')}>
                        <td className="py-5 px-6 border-b border-gray-50/50 font-black text-gray-900 text-[11px] uppercase tracking-tighter">{l.kategoriAd}</td>
                        <td className="py-5 px-4 border-b border-gray-50/50 text-center">
                          <span className="bg-gray-100 text-gray-900 font-black text-[11px] px-4 py-2 rounded-xl border border-gray-200/50 shadow-inner group-hover:bg-white transition-colors">{l.adet}</span>
                        </td>
                        <td className="py-5 px-4 border-b border-gray-50/50">
                           <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full bg-blue-400 shadow-sm" />
                              <span className="text-[10px] font-black text-gray-700 tracking-widest uppercase">{l.personelAdSoyad || <span className="opacity-20">—</span>}</span>
                           </div>
                        </td>
                        <td className="py-5 px-4 border-b border-gray-50/50">
                          <span className={'text-[9px] font-black px-3 py-1.5 rounded-full inline-flex uppercase tracking-widest border transition-all ' + (LAUNDRY_DURUM_RENK[l.durum] || 'bg-gray-100/10 text-gray-500 border-gray-200/20')}>
                            {l.durum}
                          </span>
                        </td>
                        <td className="py-5 px-4 border-b border-gray-50/50 text-[10px] font-bold text-gray-400 uppercase tracking-widest">{l.tarih}</td>
                        <td className="py-5 px-4 border-b border-gray-50/50 text-center">
                          <button onClick={function() { setLaundryModal(l); }}
                            className="text-[10px] font-black px-4 py-2 bg-white border border-gray-200 text-gray-600 hover:bg-purple-600 hover:text-white hover:border-purple-600 rounded-xl transition-all shadow-sm active:scale-95 transform-gpu uppercase tracking-widest">
                            Detay
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {laundry.length > 0 && (
                  <tfoot>
                    <tr className="bg-purple-600 text-white font-black text-[11px] shadow-2xl relative z-10">
                      <td className="py-8 px-10 uppercase tracking-[0.3em] opacity-80 border-r border-white/10">TOPLAM İŞLEM</td>
                      <td className="py-8 px-4 text-center border-r border-white/10 text-lg tabular-nums">
                        {laundry.reduce(function(s, l) { return s + l.adet; }, 0)}
                      </td>
                      <td colSpan={4} className="py-8 px-10 text-right opacity-60">
                         LAUNDRY VERİ SETİ
                      </td>
                    </tr>
                  </tfoot>
                )}
                </table>
              </div>
            </div>
          </div>
        )}

      {/* Modallar */}
      {odaModal && (
        <OdaDurumModal oda={odaModal} onKaydet={handleOdaKaydet} onKapat={function() { setOdaModal(null); }} saving={odaSaving} />
      )}
      {laundryModal && (
        <LaundryModal
          kayit={laundryModal === 'ekle' ? null : laundryModal}
          personelListe={personeller}
          laundryKalemleri={laundryKalemleri}
          onKaydet={handleLaundryKaydet}
          onSil={handleLaundrySil}
          onKapat={function() { setLaundryModal(null); }}
          saving={laundrySaving}
        />
      )}

      <UsageInfo
        color="teal"
        description="Housekeeping (Kat Hizmetleri) modülü ile odalarınızın temizlik takibini, personellerin anlık iş atamalarını ve çamaşırhane (Laundry) giriş-çıkışlarınızı tek bir havuzdan yönetebilirsiniz."
        scenarios={[
          { title: "Çoklu Oda Güncellemesi (Bulk İşlem)", content: "Masaüstü görünümde temizliği tamamlanan onlarca odayı tek tek tıklamak yerine, yanlarındaki yuvarlak kutucuktan (Checkbox) seçip yukarıda belirecek olan Mavi 'Toplu Durum Güncelle' panosu ile saniyeler içerisinde hepsini 'Temiz' durumuna alabilirsiniz." },
          { title: "Laundry Maliyet Takibi", content: "Laundry (Çamaşırhane) sekmesini seçerek gün içerisinde otel malına harcanan temizlik eforunu ve hangi personelin çamaşır sepetlerini teslim aldığını listeleyebilirsiniz." },
          { title: "Resepsiyon ile Senkronizasyon", content: "Bu sekmede (Housekeeping) kırmızı renkle işaretlenmiş olan 'Kirli' odalar Resepsiyon ekranındaki 'Oda Blokajları' kısmında kırmızı alarm verecektir. Bir odayı temiz durumuna almak resepsiyondaki rezervasyon kilidini açar." }
        ]}
      />
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fadeIn 0.4s cubic-bezier(.22,1,.36,1) forwards; }
        .animate-modal-in { animation: modalIn 0.3s cubic-bezier(.22,1,.36,1) forwards; }
        @keyframes modalIn { from { opacity: 0; transform: scale(0.95) translateY(10px); } to { opacity: 1; transform: scale(1) translateY(0); } }
        .animate-shake { animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both; }
        @keyframes shake { 10%, 90% { transform: translate3d(-1px, 0, 0); } 20%, 80% { transform: translate3d(2px, 0, 0); } 30%, 50%, 70% { transform: translate3d(-4px, 0, 0); } 40%, 60% { transform: translate3d(4px, 0, 0); } }
        .animate-slide-up { animation: slideUp 0.4s cubic-bezier(.22,1,.36,1) forwards; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
      `}</style>
    </div>
  );
}
